import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import ReactSelect, { SingleValue } from "react-select";
import { GripVertical, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PageHeader from "../../components/PageHeader";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { hasFullAccessUser } from "../../lib/screenAccess";
import { useLojas } from "../../hooks/useLojas";
import {
  filaPizzasService,
  type RodizioSaborItem,
} from "../../services/filaPizzasService";
import {
  LABEL_CLASS,
  SELECT_STYLES,
} from "../../components/DateFilters/constants";
import { useFilaIntegracaoEvents } from "../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../constants/rankingEventQueryKeys";

type RodizioTipo = "salgada" | "doce";

interface LojaOption {
  value: number;
  label: string;
}

const RodizioSequenciaPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canPickLoja = hasFullAccessUser(user);
  const { data: lojas = [] } = useLojas();
  const [tipo, setTipo] = useState<RodizioTipo>("salgada");
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [items, setItems] = useState<RodizioSaborItem[]>([]);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [nomeInput, setNomeInput] = useState("");

  useEffect(() => {
    if (selectedLojaId === undefined && user?.lojaId != null) {
      setSelectedLojaId(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  const effectiveLojaId = canPickLoja
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;

  useFilaIntegracaoEvents(effectiveLojaId, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.rodizio,
  });

  const lojaOptions = useMemo<LojaOption[]>(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );
  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  const saboresQueryKey = ["rodizio-sabores", tipo] as const;

  const { data: sabores = [], isLoading: isLoadingSabores } = useQuery({
    queryKey: saboresQueryKey,
    queryFn: () => filaPizzasService.listRodizioSabores(tipo),
  });

  useEffect(() => {
    setItems(sabores);
  }, [sabores]);

  const { data: estado, isLoading: isLoadingEstado } = useQuery({
    queryKey: ["rodizio-estado", effectiveLojaId],
    queryFn: () => filaPizzasService.getRodizioEstado(effectiveLojaId),
    enabled: effectiveLojaId != null,
  });

  const invalidateSabores = () => {
    void queryClient.invalidateQueries({ queryKey: ["rodizio-sabores"] });
    void queryClient.invalidateQueries({ queryKey: ["rodizio-estado"] });
  };

  const createMutation = useMutation({
    mutationFn: (nome: string) =>
      filaPizzasService.createRodizioSabor({ tipo, nome }),
    onSuccess: () => {
      toast.success("Sabor adicionado à sequência.");
      setModalOpen(false);
      setNomeInput("");
      setEditingId(null);
      invalidateSabores();
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Erro ao adicionar sabor.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, nome }: { id: number; nome: string }) =>
      filaPizzasService.updateRodizioSabor(id, { nome }),
    onSuccess: () => {
      toast.success("Sabor atualizado.");
      setModalOpen(false);
      setNomeInput("");
      setEditingId(null);
      invalidateSabores();
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Erro ao editar sabor.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => filaPizzasService.deleteRodizioSabor(id),
    onSuccess: () => {
      toast.success("Sabor removido da sequência.");
      invalidateSabores();
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Erro ao remover sabor.");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (nextItems: RodizioSaborItem[]) =>
      filaPizzasService.reorderRodizioSabores({
        tipo,
        ids: nextItems.map((item) => item.id),
      }),
    onSuccess: (next) => {
      setItems(next);
      queryClient.setQueryData(saboresQueryKey, next);
      toast.success("Ordem atualizada.");
      void queryClient.invalidateQueries({ queryKey: ["rodizio-estado"] });
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Erro ao reordenar.");
      setItems(sabores);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      filaPizzasService.resetRodizioSequencia({
        tipo,
        lojaId: effectiveLojaId,
      }),
    onSuccess: (nextEstado) => {
      queryClient.setQueryData(["rodizio-estado", effectiveLojaId], nextEstado);
      void queryClient.invalidateQueries({ queryKey: ["rodizio-urgencias"] });
      const next =
        tipo === "salgada"
          ? nextEstado.salgada.proximoSabor
          : nextEstado.doce.proximoSabor;
      toast.success(
        next
          ? `Fila resetada. Próximo: ${next}`
          : "Fila resetada (urgências e pendentes cancelados)."
      );
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Erro ao resetar a sequência.");
    },
  });

  const estadoTipo = tipo === "salgada" ? estado?.salgada : estado?.doce;
  const proximoIndice = estadoTipo?.proximoIndice ?? 0;
  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    reorderMutation.isPending ||
    resetMutation.isPending;

  const openCreateModal = () => {
    setEditingId(null);
    setNomeInput("");
    setModalOpen(true);
  };

  const openEditModal = (item: RodizioSaborItem) => {
    setEditingId(item.id);
    setNomeInput(item.nome);
    setModalOpen(true);
  };

  const handleSave = () => {
    const nome = nomeInput.trim();
    if (!nome) {
      toast.error("Informe o nome do sabor.");
      return;
    }
    if (editingId != null) {
      updateMutation.mutate({ id: editingId, nome });
    } else {
      createMutation.mutate(nome);
    }
  };

  const handleDelete = async (item: RodizioSaborItem) => {
    const result = await Swal.fire({
      title: "Remover sabor?",
      text: `"${item.nome}" sairá da sequência do rodízio.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b91c1c",
    });
    if (!result.isConfirmed) return;
    deleteMutation.mutate(item.id);
  };

  const handleResetSequencia = async () => {
    if (effectiveLojaId == null) {
      toast.error("Selecione uma loja.");
      return;
    }
    const primeiro = items[0]?.nome;
    const result = await Swal.fire({
      title: "Resetar fila?",
      html: primeiro
        ? `A fila <strong>${tipo === "salgada" ? "salgada" : "doce"}</strong> desta loja será zerada:<br/>• sequência volta para <strong>${primeiro}</strong><br/>• urgências e itens pendentes serão cancelados`
        : `A fila <strong>${tipo === "salgada" ? "salgada" : "doce"}</strong> desta loja será zerada (sequência, urgências e pendentes).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Resetar tudo",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#b91c1c",
    });
    if (!result.isConfirmed) return;
    resetMutation.mutate();
  };

  const moverItem = (fromId: number, toId: number) => {
    if (fromId === toId || reorderMutation.isPending) return;
    const fromIndex = items.findIndex((item) => item.id === fromId);
    const toIndex = items.findIndex((item) => item.id === toId);
    if (fromIndex < 0 || toIndex < 0) return;

    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setItems(next);
    reorderMutation.mutate(next);
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Rodízio — Sequência"
        description="Gerencie os sabores e a ordem da fila compartilhada da loja."
      />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTipo("salgada")}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              tipo === "salgada"
                ? "bg-primary text-white"
                : "border border-neutral-300 bg-white text-neutral-700"
            }`}
          >
            Salgado
          </button>
          <button
            type="button"
            onClick={() => setTipo("doce")}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              tipo === "doce"
                ? "bg-primary text-white"
                : "border border-neutral-300 bg-white text-neutral-700"
            }`}
          >
            Doce
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          {canPickLoja && (
            <div className="min-w-[220px]">
              <label className={LABEL_CLASS}>Loja</label>
              <ReactSelect
                options={lojaOptions}
                value={selectedLojaOption}
                onChange={(option: SingleValue<LojaOption>) =>
                  setSelectedLojaId(option?.value)
                }
                styles={SELECT_STYLES}
                placeholder="Selecione a loja"
              />
            </div>
          )}
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus size={16} />
            Adicionar sabor
          </button>
          <Link
            to="/rodizio/urgencia"
            className="inline-flex items-center justify-center rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5"
          >
            Ir para Urgência
          </Link>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          {isLoadingEstado ? (
            <p>Carregando próximo sabor...</p>
          ) : (
            <>
              <p>
                Próximo a montar:{" "}
                <strong>{estadoTipo?.proximoSabor ?? "—"}</strong>
                {estadoTipo?.isUrgente ? (
                  <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium text-white">
                    Urgência
                  </span>
                ) : null}
              </p>
              {estadoTipo?.isUrgente ? (
                <p className="text-blue-700/90">
                  Depois retoma a sequência em:{" "}
                  <strong>
                    {estadoTipo.proximoSaborSequencia ?? "—"}
                  </strong>
                </p>
              ) : null}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => void handleResetSequencia()}
          disabled={isSaving || effectiveLojaId == null}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RotateCcw size={15} />
          {resetMutation.isPending ? "Resetando..." : "Resetar fila"}
        </button>
      </div>

      {reorderMutation.isPending && (
        <p className="mb-3 text-sm text-neutral-500">Salvando nova ordem…</p>
      )}

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
        {isLoadingSabores ? (
          <p className="px-4 py-8 text-center text-neutral-500">
            Carregando sabores...
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-8 text-center text-neutral-500">
            Nenhum sabor na sequência. Adicione o primeiro sabor.
          </p>
        ) : (
          <ol className="divide-y divide-neutral-100">
            {items.map((sabor, index) => {
              const isSequenciaNext =
                items.length > 0 && index === proximoIndice % items.length;
              const isUrgenciaNext =
                Boolean(estadoTipo?.isUrgente) &&
                sabor.nome === estadoTipo?.proximoSabor;
              const isNext = isUrgenciaNext || (!estadoTipo?.isUrgente && isSequenciaNext);
              return (
                <li
                  key={sabor.id}
                  draggable={!isSaving}
                  onDragStart={() => setDraggingId(sabor.id)}
                  onDragEnd={() => setDraggingId(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggingId != null) {
                      moverItem(draggingId, sabor.id);
                    }
                    setDraggingId(null);
                  }}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm sm:gap-3 sm:px-4 sm:py-3 ${
                    draggingId === sabor.id
                      ? "bg-primary/5 opacity-60"
                      : isUrgenciaNext
                        ? "bg-amber-50 font-semibold text-amber-900"
                        : isNext
                          ? "bg-primary/5 font-semibold text-primary"
                          : isSequenciaNext && estadoTipo?.isUrgente
                            ? "bg-blue-50/80 text-blue-900"
                            : "text-neutral-800 hover:bg-neutral-50"
                  }`}
                >
                  <button
                    type="button"
                    draggable={!isSaving}
                    onDragStart={(e) => {
                      e.stopPropagation();
                      setDraggingId(sabor.id);
                    }}
                    onDragEnd={() => setDraggingId(null)}
                    className="inline-flex cursor-grab items-center justify-center rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 active:cursor-grabbing"
                    title="Arrastar para reordenar"
                    aria-label={`Reordenar ${sabor.nome}`}
                    disabled={isSaving}
                  >
                    <GripVertical size={18} />
                  </button>
                  <span className="w-8 tabular-nums text-neutral-400">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{sabor.nome}</span>
                  {isUrgenciaNext && (
                    <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium text-white">
                      Urgência
                    </span>
                  )}
                  {isSequenciaNext && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium text-white ${
                        estadoTipo?.isUrgente ? "bg-blue-600" : "bg-primary"
                      }`}
                    >
                      {estadoTipo?.isUrgente ? "Retoma aqui" : "Próximo"}
                    </span>
                  )}
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(sabor)}
                      disabled={isSaving}
                      className="inline-flex items-center justify-center rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
                      title="Editar"
                      aria-label={`Editar ${sabor.nome}`}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(sabor)}
                      disabled={isSaving}
                      className="inline-flex items-center justify-center rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                      title="Remover"
                      aria-label={`Remover ${sabor.nome}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          if (isSaving) return;
          setModalOpen(false);
          setEditingId(null);
          setNomeInput("");
        }}
        title={editingId != null ? "Editar sabor" : "Adicionar sabor"}
        panelClassName="max-w-md"
        compactHeader
      >
        <label className={LABEL_CLASS} htmlFor="rodizio-sabor-nome">
          Nome do sabor
        </label>
        <input
          id="rodizio-sabor-nome"
          type="text"
          value={nomeInput}
          onChange={(e) => setNomeInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSave();
            }
          }}
          className="mb-4 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          placeholder="Ex.: CALABRESA"
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={isSaving}
            onClick={() => {
              setModalOpen(false);
              setEditingId(null);
              setNomeInput("");
            }}
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSave}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
          >
            {isSaving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </Modal>
    </main>
  );
};

export default RodizioSequenciaPage;
